import { registerUser } from "@/api/modules";
import { validateReferralCode } from "@/api/modules/users.api";
import { NariShaktiLogo } from "@/assets";
import { FloatingActionButtons } from "@/components/ui/FloatingActionButtons";
import { LoginMethodStep } from "@/features/registration/LoginMethodStep";
import { NameStep } from "@/features/registration/NameStep";
import { PasswordStep } from "@/features/registration/PasswordStep";
import { ReferralStep } from "@/features/registration/ReferralStep";
import { ReviewStep } from "@/features/registration/ReviewStep";
import { LoginMethod, RegisterPayload } from "@/types/auth";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

const steps = ["name", "loginMethod", "referral", "password", "review"];

interface FormData {
  name: string;
  loginMethod: LoginMethod;
  email: string;
  phone: string;
  aadhar: string;
  referralCode: string;
  password: string;
  confirmPassword: string;
}

export default function RegistrationStepper() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registeredMethod, setRegisteredMethod] = useState<LoginMethod | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uplineName, setUplineName] = useState<string>("");
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [referralError, setReferralError] = useState<string>("");
  const [isReferralFromUrl, setIsReferralFromUrl] = useState(false);

  // React Hook Form setup
  const { control, handleSubmit, watch, setValue, trigger } = useForm<FormData>(
    {
      defaultValues: {
        name: "",
        loginMethod: LoginMethod.EMAIL,
        email: "",
        phone: "",
        aadhar: "",
        referralCode: "",
        password: "",
        confirmPassword: "",
      },
      mode: "onChange",
    }
  );

  const watchedValues = watch();

  // Check for pre-filled referral code from URL
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      setValue("referralCode", refCode);
      setIsReferralFromUrl(true);
      // Validate the pre-filled referral code
      validateReferralCodeAsync(refCode);
    }
  }, [searchParams, setValue]);

  // Debounced referral code validation
  useEffect(() => {
    const referralCode = watchedValues.referralCode;
    if (!referralCode || referralCode.length < 10) {
      setUplineName("");
      setReferralError("");
      return;
    }

    const timer = setTimeout(() => {
      validateReferralCodeAsync(referralCode);
    }, 500);

    return () => clearTimeout(timer);
  }, [watchedValues.referralCode]);

  const validateReferralCodeAsync = async (code: string) => {
    if (!code || code.length < 10) return;

    setValidatingReferral(true);
    setReferralError("");
    setUplineName("");

    try {
      const response = await validateReferralCode(code);
      console.log("Referral validation response:", response);
      // Handle nested data structure: response.data.data
      if (response.success && response.data?.data) {
        const uplineData = response.data.data;
        console.log("Setting upline name:", uplineData.fullName);
        setUplineName(uplineData.fullName);
        setReferralError("");
      } else {
        console.log("Validation failed:", response.message);
        setReferralError(
          response.message ||
            t("auth.registration.referralCodeNotFound") ||
            "Referral code not found"
        );
        setUplineName("");
      }
    } catch (err) {
      console.error("Referral validation error:", err);
      setReferralError(
        t("auth.registration.referralValidationError") ||
          "Error validating referral code"
      );
      setUplineName("");
    } finally {
      setValidatingReferral(false);
    }
  };

  const validateCurrentStep = async () => {
    const fieldsToValidate: (keyof FormData)[] = [];

    if (step === 0) {
      fieldsToValidate.push("name");
    } else if (step === 1) {
      if (watchedValues.loginMethod === "email") fieldsToValidate.push("email");
      if (watchedValues.loginMethod === "phone") fieldsToValidate.push("phone");
      if (watchedValues.loginMethod === "aadhar")
        fieldsToValidate.push("aadhar");
    } else if (step === 2) {
      fieldsToValidate.push("referralCode");
    } else if (step === 3) {
      fieldsToValidate.push("password", "confirmPassword");
    }

    const isValid = await trigger(fieldsToValidate);
    return isValid;
  };

  const next = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      setStep((s) => Math.min(s + 1, steps.length - 1));
    }
  };

  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError("");
    try {
      // Prepare payload based on selected login method
      const payload: RegisterPayload = {
        loginMethod: data.loginMethod,
        name: data.name.trim(),
        password: data.password.trim(),
        referralCode: data.referralCode.trim() || undefined,
      };
      if (data.loginMethod === "email") payload.email = data.email.trim();
      if (data.loginMethod === "phone") payload.phone = data.phone.trim();
      if (data.loginMethod === "aadhar") payload.aadhar = data.aadhar.trim();

      const res = await registerUser(payload);
      if (res.success) {
        setRegisteredMethod(data.loginMethod);
        setSuccess(true);
      } else {
        setError(
          res.message ||
            t("auth.registration.registrationFailed") ||
            "Registration failed"
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("auth.registration.registrationFailed") ||
          "Registration failed"
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-bold text-primary mb-3">
          {t("auth.registration.registrationSuccess") || "पंजीकरण सफल!"}
        </h2>
        {registeredMethod === "email" && (
          <p className="mb-2 text-primary font-semibold text-sm">
            {t("auth.registration.emailVerificationMessage") ||
              "ईमेल सत्यापन संदेश भेजा गया है"}
          </p>
        )}
        {(registeredMethod === "phone" || registeredMethod === "aadhar") && (
          <p className="mb-2 text-primary font-semibold text-sm">
            {t("auth.registration.phoneAadharApprovalMessage") ||
              "फोन/आधार अनुमोदन संदेश"}
          </p>
        )}
        <p className="text-sm">
          {t("auth.registration.pleaseLogin") || "कृपया लॉगिन करें"}{" "}
          <Link
            to="/login"
            className="text-primary underline hover:text-pink-700"
          >
            {t("auth.registration.loginHere") || "यहाँ लॉगिन करें"}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center registration-container p-4">
      <div className="relative w-full max-w-lg bg-[var(--bg-surface)] rounded-2xl shadow-xl p-6 border border-[var(--border-light)] flex flex-col justify-center">
        {/* Compact Nari Suraksha Logo at top center, overlapping border */}
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center">
          <div
            className="w-20 h-20 rounded-full border-3 border-[var(--border-medium)] bg-[var(--bg-card)] flex items-center justify-center shadow-lg ring-2 ring-[var(--border-light)]"
            aria-label="Nari Suraksha Logo"
          >
            <img
              src={NariShaktiLogo}
              alt={
                t("common.nariShaktiLogoAlt", {
                  defaultValue: "Nari Suraksha Logo",
                }) || "नारी सुरक्षा लोगो"
              }
              className="w-16 h-16 object-contain rounded-full"
              loading="eager"
            />
          </div>
        </div>

        {/* Compact Progress Bar */}
        <div className="mb-6 w-full mt-6">
          <div className="flex items-center justify-between w-full relative">
            {/* Fixed connecting line positioning */}
            <div
              className="absolute top-4 left-0 right-0 h-1.5 bg-[var(--border-light)] rounded-full z-0"
              style={{ left: "15px", right: "15px" }}
            ></div>
            <div
              className="absolute top-4 h-1.5 bg-primary rounded-full z-10 transition-all duration-700 ease-out shadow-sm"
              style={{
                left: "15px",
                width:
                  step === 0
                    ? "0%"
                    : step >= steps.length - 1
                    ? "calc(100% - 30px)"
                    : `${(step / (steps.length - 1)) * 100}%`,
              }}
            ></div>

            {steps.map((label, i) => (
              <div
                key={label}
                className="flex flex-col items-center z-20"
                style={{ minWidth: "60px" }}
              >
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full border-2 font-bold text-xs transition-all duration-300 mb-1 ${
                    i <= step
                      ? "bg-primary border-primary text-white shadow-md ring-1 ring-primary/30"
                      : "bg-[var(--bg-card)] border-[var(--border-light)] text-[var(--text-secondary)] hover:border-[var(--border-medium)] transition-colors shadow-sm"
                  }`}
                  aria-current={i === step ? "step" : undefined}
                >
                  {i < step ? (
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <div
                  className={`text-xs font-medium text-center px-1 transition-colors duration-300 ${
                    i === step
                      ? "text-primary font-semibold"
                      : "text-[var(--text-secondary)]"
                  }`}
                >
                  {t(`auth.registration.${label}`) || label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compact Step Forms */}
        <div className="flex-1 flex flex-col justify-center">
          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit((data) => {
              if (step === steps.length - 1) {
                onSubmit(data);
              } else {
                next();
              }
            })}
          >
            {/* Step 0: Name */}
            {step === 0 && <NameStep control={control} />}

            {/* Step 1: Compact Login Method */}
            {/* Step 1: Login Method */}
            {step === 1 && (
              <LoginMethodStep
                formData={watchedValues}
                onUpdateField={(field, value) => setValue(field, value)}
                control={control}
                onNext={next}
                onPrev={prev}
              />
            )}

            {/* Step 2: Referral Code (required) */}
            {step === 2 && (
              <ReferralStep
                formData={watchedValues}
                onUpdateField={(field, value) => setValue(field, value)}
                control={control}
                uplineName={uplineName}
                validatingReferral={validatingReferral}
                referralError={referralError}
                isReferralFromUrl={isReferralFromUrl}
                onNext={next}
                onPrev={prev}
              />
            )}

            {/* Step 3: Password */}
            {step === 3 && (
              <PasswordStep
                formData={watchedValues}
                onUpdateField={(field, value) => setValue(field, value)}
                control={control}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
                onToggleConfirmPassword={() =>
                  setShowConfirmPassword(!showConfirmPassword)
                }
                onNext={next}
                onPrev={prev}
              />
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <ReviewStep
                formData={watchedValues}
                onUpdateField={(field, value) => setValue(field, value)}
                control={control}
                onNext={next}
                onPrev={prev}
              />
            )}

            {/* Global error */}
            {error && (
              <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-3 py-2 rounded text-center animate-pulse text-sm">
                {error}
              </div>
            )}

            {/* Compact Navigation Buttons */}
            <div className="flex justify-between items-center mt-4 gap-3">
              {step > 0 && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg font-semibold border border-primary/30 text-primary bg-[var(--bg-card)] hover:bg-primary/10 hover:border-primary/50 transition-all duration-300 disabled:opacity-50 shadow-sm hover:shadow-md flex items-center gap-1 text-sm"
                  onClick={prev}
                  disabled={loading}
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  {t("auth.registration.back") || "वापस"}
                </button>
              )}
              <div className="flex-1 text-right">
                {step < steps.length - 1 ? (
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg font-semibold bg-primary text-white hover:bg-pink-700 transition-all duration-300 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-1 ml-auto border border-primary text-sm"
                    disabled={loading}
                  >
                    {t("auth.registration.next") || "आगे"}
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg font-semibold bg-primary text-white hover:bg-pink-700 transition-all duration-300 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-1 ml-auto border border-primary text-sm"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t("auth.registration.registering")}
                      </>
                    ) : (
                      <>
                        {t("auth.registration.submit")}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Compact Login Link for Step 0 */}
            {step === 0 && (
              <div className="mt-4 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                  <span className="text-xs text-gray-600">
                    {t("auth.registration.alreadyRegistered") ||
                      "पहले से पंजीकृत हैं?"}
                  </span>
                  <Link
                    to="/login"
                    className="text-primary hover:text-pink-600 font-semibold text-xs transition-colors duration-300 flex items-center gap-1"
                  >
                    {t("auth.registration.loginHere") || "यहाँ लॉगिन करें"}
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
      <FloatingActionButtons />
    </div>
  );
}
